// src/constants/Games.ts
import { Path } from './Path';
import { ActivityGame } from '../services/activity';

import lettersIcons from '../assets/icons/games/letters';
import repeatIcons from '../assets/icons/games/repeat';
import findIcons from '../assets/icons/games/find';
import pickIcons from '../assets/icons/games/pick';
import pairIcons from '../assets/icons/games/pair';
import memoryIcons from '../assets/icons/games/memory';
import recognizeIcons from '../assets/icons/games/recognize';
import recognizeIcons2 from '../assets/icons/games/recognize2';
import draglettersIcons from '../assets/icons/games/dragletters';
import writingIcons from '../assets/icons/games/writing';
import repeatActiveLime from '../assets/icons/games/repeat/play-circle-filled-lime.svg';
import findActiveLime from '../assets/icons/games/find/volume-filled-lime.svg';
import pickActiveLime from '../assets/icons/games/pick/image-check-filled-lime.svg';
import pairActiveLime from '../assets/icons/games/pair/route-filled-lime.svg';
import memoryActiveLime from '../assets/icons/games/memory/elements-filled-lime.svg';
import recognizeActiveLime from '../assets/icons/games/recognize/eye-filled-lime.svg';
import recognize2ActiveLime from '../assets/icons/games/recognize2/eye-filled2-lime.svg';
import draglettersActiveLime from '../assets/icons/games/dragletters/arrows-move-filled-lime.svg';
import writingActiveLime from '../assets/icons/games/writing/pen-active-lime.svg';

// Aktywne ikony gier Easy w kolorze lime — jaśniejsze i lepiej widoczne na turkusie.
// Oryginały (limegreen) zostają w grupie Regular; aktywna ikona Literuj już jest w kolorze lime (w obu grupach).
const kiddoRepeatIcons = { ...repeatIcons, active: repeatActiveLime };
const kiddoFindIcons = { ...findIcons, active: findActiveLime };
const kiddoPickIcons = { ...pickIcons, active: pickActiveLime };
const kiddoPairIcons = { ...pairIcons, active: pairActiveLime };
const kiddoMemoryIcons = { ...memoryIcons, active: memoryActiveLime };
const kiddoRecognizeIcons = { ...recognizeIcons, active: recognizeActiveLime };
const kiddoRecognizeIcons2 = { ...recognizeIcons2, active: recognize2ActiveLime };
const kiddoDraglettersIcons = { ...draglettersIcons, active: draglettersActiveLime };
const kiddoWritingIcons = { ...writingIcons, active: writingActiveLime };

// Gry dzielą się na dwie grupy: KIDDO to Easy (kod w src/views/kiddo-games), OLDER to Regular.
// W menu widać zawsze tylko jedną z nich.
export enum GameGroup {
  KIDDO = 'kiddo',
  OLDER = 'older',
}

type Game = {
  path: Path;
  // Na przycisku w menu numer gry albo — dla gry spoza numeracji — napis (klucz tłumaczenia)
  number?: number;
  label?: string;
  // Klucz nazwy gry w tłumaczeniach (games:names); w podpowiedzi menu MenuButton dokłada przed nią numer
  text: string;
  icons: {
    main: string,
    active: string,
    disabled: string,
  };
  // Przycisk w innym kolorze niż pozostałe gry
  accent?: boolean;
};

// Kolejność jak w menu. Obie grupy mają prawie te same gry: w Regular nie ma Ułóż słowa, a Memory jest w Easy trzecia,
// w Regular piąta (Wybierz i Sparuj stoją w Easy o jedno miejsce dalej). Pierwsza gra grupy otwiera się
// po wyborze listy. Na końcu obu grup stoi Literuj — bez numeru, z napisem SPELL i w innym kolorze. Jest poza
// kolejką: NEXT GAME prowadzi przez gry z numerami, po Napisz jest zakończenie, a Literuj otwiera się tylko z menu.
// Następną grę wskazuje każda gra sama (useGoToRoute w jej widoku) — przy zmianie kolejności trzeba ją
// poprawić także tam.
// Gry Easy to osobne kopie gier Regular (Literuj odwrotnie: w Regular jest kopia gry z Easy) — na razie z ikonami
// jak w Regular, tylko aktywne są jaśniejsze.
export const GAMES: Record<GameGroup, Game[]> = {
  [GameGroup.KIDDO]: [
    {
      path: Path.KIDDO_REPEAT, number: 1, text: 'games:names.repeat', icons: kiddoRepeatIcons,
    },
    {
      path: Path.KIDDO_FIND, number: 2, text: 'games:names.find', icons: kiddoFindIcons,
    },
    {
      path: Path.KIDDO_MEMORY, number: 3, text: 'games:names.memory', icons: kiddoMemoryIcons,
    },
    {
      path: Path.KIDDO_PICK, number: 4, text: 'games:names.pick', icons: kiddoPickIcons,
    },
    {
      path: Path.KIDDO_PAIRING, number: 5, text: 'games:names.pairing', icons: kiddoPairIcons,
    },
    {
      path: Path.KIDDO_RECOGNIZE, number: 6, text: 'games:names.recognizeEasy', icons: kiddoRecognizeIcons,
    },
    {
      path: Path.KIDDO_RECOGNIZE2, number: 7, text: 'games:names.recognize2Easy', icons: kiddoRecognizeIcons2,
    },
    {
      path: Path.KIDDO_DRAGLETTERS, number: 8, text: 'games:names.dragLetters', icons: kiddoDraglettersIcons,
    },
    {
      path: Path.KIDDO_WRITING, number: 9, text: 'games:names.writing', icons: kiddoWritingIcons,
    },
    {
      path: Path.KIDDO_LETTERS, label: 'games:buttons.spell', text: 'games:names.letters', icons: lettersIcons, accent: true,
    },
  ],
  [GameGroup.OLDER]: [
    {
      path: Path.REPEAT, number: 1, text: 'games:names.repeat', icons: repeatIcons,
    },
    {
      path: Path.FIND, number: 2, text: 'games:names.find', icons: findIcons,
    },
    {
      path: Path.PICK, number: 3, text: 'games:names.pick', icons: pickIcons,
    },
    {
      path: Path.PAIRING, number: 4, text: 'games:names.pairing', icons: pairIcons,
    },
    {
      path: Path.MEMORY, number: 5, text: 'games:names.memory', icons: memoryIcons,
    },
    {
      path: Path.RECOGNIZE, number: 6, text: 'games:names.recognize', icons: recognizeIcons,
    },
    {
      path: Path.RECOGNIZE2, number: 7, text: 'games:names.recognize2', icons: recognizeIcons2,
    },
    {
      path: Path.WRITING, number: 8, text: 'games:names.writing', icons: writingIcons,
    },
    {
      path: Path.LETTERS, label: 'games:buttons.spell', text: 'games:names.letters', icons: lettersIcons, accent: true,
    },
  ],
};

// Gra w menu po nazwie, pod którą wysyła odpowiedzi do monitoringu aktywności (src/services/activity.ts). Po niej okno
// „Aktywność” rozpoznaje gry ucznia i układa je jak menu. TypeScript wymaga tu każdej nazwy, więc nowej gry nie da się
// pominąć
export const ACTIVITY_GAME_PATHS: Record<ActivityGame, Path> = {
  [ActivityGame.REPEAT]: Path.REPEAT,
  [ActivityGame.REPEAT_EASY]: Path.KIDDO_REPEAT,
  [ActivityGame.FIND]: Path.FIND,
  [ActivityGame.FIND_EASY]: Path.KIDDO_FIND,
  [ActivityGame.PICK]: Path.PICK,
  [ActivityGame.PICK_EASY]: Path.KIDDO_PICK,
  [ActivityGame.PAIRING]: Path.PAIRING,
  [ActivityGame.PAIRING_EASY]: Path.KIDDO_PAIRING,
  [ActivityGame.MEMORY]: Path.MEMORY,
  [ActivityGame.MEMORY_EASY]: Path.KIDDO_MEMORY,
  [ActivityGame.RECOGNIZE]: Path.RECOGNIZE,
  [ActivityGame.RECOGNIZE_EASY]: Path.KIDDO_RECOGNIZE,
  [ActivityGame.RECOGNIZE2]: Path.RECOGNIZE2,
  [ActivityGame.RECOGNIZE2_EASY]: Path.KIDDO_RECOGNIZE2,
  [ActivityGame.WRITING]: Path.WRITING,
  [ActivityGame.WRITING_EASY]: Path.KIDDO_WRITING,
  [ActivityGame.DRAG_LETTERS_EASY]: Path.KIDDO_DRAGLETTERS,
  [ActivityGame.LETTERS]: Path.LETTERS,
  [ActivityGame.LETTERS_EASY]: Path.KIDDO_LETTERS,
};

// Grupa gry otwartej pod danym adresem; poza grami — undefined.
export const getGameGroup = (pathname: string): GameGroup | undefined => {
  const gamePath = pathname.replace(/\/$/, '');

  return Object.values(GameGroup).find((group) => GAMES[group].some(({ path }) => path === gamePath));
};
